import { useEffect, useState } from 'react';
import { Checkbox, Form, Input, InputNumber, Modal, Radio, Select, Space, message as toast } from 'antd';
import dayjs from 'dayjs';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import UserPicker, { UserPickerValue } from 'pages/star-shared/components/user-picker';
import { useFeed } from './shared/context';
import { RESTRICT_DAY_OPTIONS, RESTRICT_SCOPE_OPTIONS } from './shared/labels';
import { FeedRestrictionRow, RestrictionScope } from './shared/types';
import { feedError } from './shared/utils';

/** Thời hạn hạn chế: 1/3/7/30 ngày, vô thời hạn, hoặc tự nhập 1–3650 ngày. */
export interface DurationValue {
  mode: string; // '1' | '3' | '7' | '30' | 'forever' | 'custom'
  custom: number;
}

export const DEFAULT_DURATION: DurationValue = { mode: '3', custom: 14 };

export const durationDays = (v: DurationValue): number | null =>
  v.mode === 'forever' ? null : v.mode === 'custom' ? v.custom : Number(v.mode);

export const DurationPicker = ({
  value,
  onChange,
}: {
  value: DurationValue;
  onChange: (v: DurationValue) => void;
}) => (
  <Space wrap>
    <Select
      value={value.mode}
      style={{ width: 160 }}
      options={RESTRICT_DAY_OPTIONS}
      onChange={(mode) => onChange({ ...value, mode })}
    />
    {value.mode === 'custom' && (
      <InputNumber
        min={1}
        max={3650}
        value={value.custom}
        onChange={(v) => onChange({ ...value, custom: Math.min(3650, Math.max(1, Number(v) || 1)) })}
        addonAfter="ngày"
        style={{ width: 150 }}
      />
    )}
  </Space>
);

/** "đến 05/10 14:30" / "vô thời hạn" — câu cho toast sau khi hạn chế. */
export const untilText = (endsAt: string | null | undefined, days: number | null) => {
  if (endsAt) return `đến ${dayjs(endsAt).format('DD/MM HH:mm')}`;
  if (days === null) return 'vô thời hạn';
  return `đến ${dayjs().add(days, 'day').format('DD/MM HH:mm')}`;
};

const pickerName = (v?: UserPickerValue) => (v?.label ? String(v.label).split(' — ')[0] : '');

/** D18 — hạn chế một người (03 §8.4). Backend chặn hạn chế người bậc ≥ mình (409 FEED_ADMIN_ONLY). */
const RestrictModal = ({
  open,
  presetUser,
  defaultScope = 'POST',
  onClose,
}: {
  open: boolean;
  presetUser?: { userId: number; name: string } | null;
  defaultScope?: RestrictionScope;
  onClose: () => void;
}) => {
  const { afterAction } = useFeed();
  const [user, setUser] = useState<UserPickerValue>();
  const [scope, setScope] = useState<RestrictionScope>(defaultScope);
  const [duration, setDuration] = useState<DurationValue>(DEFAULT_DURATION);
  const [reason, setReason] = useState('');
  const [hideExisting, setHideExisting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUser(presetUser ? { value: presetUser.userId, label: presetUser.name } : undefined);
    setScope(defaultScope);
    setDuration(DEFAULT_DURATION);
    setReason('');
    setHideExisting(false);
  }, [open, presetUser, defaultScope]);

  const trimmed = reason.trim();
  const valid = !!user?.value && trimmed.length >= 3 && trimmed.length <= 500;

  const submit = async () => {
    if (!valid || !user) return;
    setSaving(true);
    const days = durationDays(duration);
    try {
      const res: any = await AxiosUtils.post(API.feedAdminRestrictions, {
        userId: user.value,
        scope,
        days,
        reason: trimmed,
        hideExistingPosts: hideExisting,
      });
      const row = res?.data as FeedRestrictionRow | undefined;
      const name = row?.user?.name || pickerName(user) || `#${user.value}`;
      const until = untilText(row?.endsAt, days);
      toast.success(until === 'vô thời hạn' ? `Đã hạn chế ${name} vô thời hạn.` : `Đã hạn chế ${name} ${until}.`);
      onClose();
      afterAction();
    } catch (err) {
      toast.error(feedError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      width={560}
      title="Hạn chế một người"
      onCancel={onClose}
      onOk={submit}
      okText="Hạn chế"
      okButtonProps={{ danger: true, disabled: !valid }}
      cancelText="Huỷ"
      confirmLoading={saving}
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item label="Người" required>
          <UserPicker value={user} onChange={setUser} disabled={!!presetUser} size="middle" />
        </Form.Item>
        <Form.Item label="Không được">
          <Radio.Group value={scope} onChange={(e) => setScope(e.target.value)}>
            <Space direction="vertical" size={4}>
              {RESTRICT_SCOPE_OPTIONS.map((o) => (
                <Radio key={o.value} value={o.value}>
                  {o.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="Trong">
          <DurationPicker value={duration} onChange={setDuration} />
        </Form.Item>
        <Form.Item
          label="Lý do"
          required
          extra="Người bị hạn chế sẽ thấy lý do này."
          validateStatus={reason && trimmed.length < 3 ? 'error' : undefined}
          help={reason && trimmed.length < 3 ? 'Lý do tối thiểu 3 ký tự.' : undefined}
        >
          <Input.TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 5 }}
            maxLength={500}
            showCount
          />
        </Form.Item>
        <Checkbox checked={hideExisting} onChange={(e) => setHideExisting(e.target.checked)}>
          Ẩn luôn mọi bài đang hiện của người này
        </Checkbox>
      </Form>
    </Modal>
  );
};

export default RestrictModal;
